import {
  del,
  get,
  patch,
  post,
  type RequestConfig,
} from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import type {
  ItemCategoryPayload,
  ItemCategoryRecord,
  ItemCategoryType,
} from "@project/types/item-category";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  unwrapItem,
} from "./list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/item-categories`;
const SILENT: RequestConfig = { showNotification: false };

class ItemCategoryService {
  listPage = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<ItemCategoryRecord>> =>
    fetchListPage<ItemCategoryRecord>(`${BASE}/`, query);

  get = async (id: number): Promise<ItemCategoryRecord | undefined> => {
    const data = await get<
      ApiItemResponse<ItemCategoryRecord> | ItemCategoryRecord
    >(`${BASE}/${id}/`, SILENT_LIST);
    return unwrapItem(data);
  };

  create = async (
    payload: ItemCategoryPayload
  ): Promise<ItemCategoryRecord> => {
    const data = await post<
      ApiItemResponse<ItemCategoryRecord> | ItemCategoryRecord,
      ItemCategoryPayload
    >(`${BASE}/`, payload, SILENT);
    const item = unwrapItem(data);
    if (!item) {
      throw new Error("Failed to create item category.");
    }
    return item;
  };

  update = async (
    id: number,
    payload: Partial<ItemCategoryPayload>
  ): Promise<ItemCategoryRecord | undefined> => {
    const data = await patch<
      ApiItemResponse<ItemCategoryRecord> | ItemCategoryRecord,
      Partial<ItemCategoryPayload>
    >(`${BASE}/${id}/`, payload, SILENT);
    return unwrapItem(data);
  };

  delete = async (id: number): Promise<void> => {
    await del(`${BASE}/${id}/`, SILENT);
  };

  listLookupByType = async (item_category_type: ItemCategoryType) => {
    const result = await this.listPage({
      page: 1,
      page_size: 100,
      item_category_type,
      status: "active",
    });
    return result.data.map(item => ({
      value: String(item.id),
      label: item.name,
    }));
  };
}

export const itemCategoryService = new ItemCategoryService();
